import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Music, Voicemail as VoicemailIcon, PhoneForwarded, Upload, Trash2, PlayCircle, PauseCircle, History, Phone, Megaphone, Users, PhoneCall, Video, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';
import * as perms from '../lib/permissions';

/**
 * Reusable section card for an admin-uploadable audio file.
 *
 * Props:
 *   title, icon, description       — UI labels
 *   currentUrl, currentFilename    — what's set now (or system default)
 *   isSystemDefault                — boolean, shows tag and disables Delete
 *   uploadEndpoint                 — POST URL (multipart/form-data, field "file")
 *   deleteEndpoint                 — DELETE URL (admin reset to system default)
 *   onChange                       — called after upload/delete to refresh parent
 *   testIdPrefix                   — used for stable test IDs
 */
const AudioFileSection = ({
  title, icon: Icon, description,
  currentUrl, currentFilename, isSystemDefault,
  uploadEndpoint, deleteEndpoint,
  onChange, testIdPrefix,
  // When true, all replace/upload/reset buttons are hidden — preview
  // remains. Used to enforce the "view-only" rule for special users
  // who have admin_calling but should not edit media.
  readOnly = false,
}) => {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const fullUrl = currentUrl ? `${backendUrl}${currentUrl}` : null;

  const onSelectFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      await api.post(uploadEndpoint, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Saved');
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Reset to system default?')) return;
    try {
      await api.delete(deleteEndpoint);
      toast.success('Reset to system default');
      onChange?.();
    } catch (e) {
      toast.error('Could not reset');
    }
  };

  const togglePreview = () => {
    const a = audioRef.current;
    if (!a) return;
    if (previewing) { a.pause(); setPreviewing(false); }
    else { a.play().then(() => setPreviewing(true)).catch(() => setPreviewing(false)); }
  };

  return (
    <Card className="rounded-2xl" data-testid={`${testIdPrefix}-card`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="w-5 h-5 text-[#586D52]" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {currentUrl ? (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 bg-white/60 dark:bg-[#1a1a1a]/60">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium truncate">{currentFilename || 'audio'}</p>
              {isSystemDefault && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#586D52]/15 text-[#586D52]">
                  System default
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-3 break-all">{currentUrl}</p>
            <audio ref={audioRef} src={fullUrl} onEnded={() => setPreviewing(false)} preload="none" />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={togglePreview} data-testid={`${testIdPrefix}-preview`}>
                {previewing ? <PauseCircle className="w-4 h-4 mr-1.5" /> : <PlayCircle className="w-4 h-4 mr-1.5" />}
                {previewing ? 'Pause' : 'Preview'}
              </Button>
              {!readOnly && (
                <>
                  <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid={`${testIdPrefix}-replace`}>
                    <Upload className="w-4 h-4 mr-1.5" />
                    Replace
                  </Button>
                  {!isSystemDefault && (
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={onDelete} data-testid={`${testIdPrefix}-delete`}>
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Reset
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
            <Icon className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground mb-3">{description}</p>
            {!readOnly && (
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-[#586D52] hover:bg-[#4a5d46]" data-testid={`${testIdPrefix}-upload`}>
                <Upload className="w-4 h-4 mr-1.5" />
                {uploading ? 'Uploading…' : 'Upload Audio'}
              </Button>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/m4a,audio/wav,audio/ogg,audio/aac,.mp3,.m4a,.wav,.ogg,.aac"
          hidden
          onChange={onSelectFile}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <p className="text-xs text-muted-foreground">
          Accepts MP3, M4A, WAV, OGG, or AAC.
        </p>
      </CardContent>
    </Card>
  );
};

const AdminCallingPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [settings, setSettings] = useState({});
  usePageHeader({ title: 'Calling' }, []);

  const isAdmin = user?.role === 'admin';
  // Special users with `admin_calling` toggle get VIEW access to this
  // page. Edit controls (hold music, voicemail greeting, please-hold
  // audio, call-start disclaimer, play-on-connect toggles) are
  // explicitly admin-only per spec — gated where they render below.
  const canAccess = isAdmin || (user?.feature_toggles || {}).admin_calling === true;
  const callPerm = perms.callingAdmin(user);

  const load = async () => {
    try {
      const res = await api.get('/calling/settings');
      setSettings(res.data || {});
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-background p-8 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-medium">Admin only</p>
        <p className="text-muted-foreground text-sm mt-1">You don't have access to this page.</p>
        <Button className="mt-4" onClick={() => navigate('/')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="admin-calling-page">
      <div className="max-w-2xl mx-auto p-3 space-y-4">
        {/* Admin nav row — History · Assignments · Routing on a single row */}
        <div className="grid grid-cols-3 gap-1.5 mb-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => navigate('/admin/master-history')}
            data-testid="master-history-link"
          >
            <History className="w-4 h-4 mr-1.5" />
            History
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => navigate('/admin/assignments')}
            data-testid="assignments-link"
          >
            <UserCheck className="w-4 h-4 mr-1.5" />
            Assignments
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => navigate('/admin/phone-routing')}
            data-testid="phone-routing-link"
          >
            <Phone className="w-4 h-4 mr-1.5" />
            Routing
          </Button>
        </div>

        <AudioFileSection
          title="Call Holding Music"
          icon={Music}
          description="No hold music configured. Calls placed on hold will fall back to silence."
          currentUrl={settings.hold_music_url}
          currentFilename={settings.hold_music_filename}
          isSystemDefault={false}
          uploadEndpoint="/admin/calling/hold-music"
          deleteEndpoint="/admin/calling/hold-music"
          onChange={load}
          testIdPrefix="hold-music"
          readOnly={!callPerm.editHoldMusic}
        />

        <AudioFileSection
          title="Default Voicemail Greeting"
          icon={VoicemailIcon}
          description="Used system-wide when a user hasn't set a custom greeting."
          currentUrl={settings.default_voicemail_url}
          currentFilename={settings.default_voicemail_filename}
          isSystemDefault={!!settings.default_voicemail_is_system}
          uploadEndpoint="/admin/calling/voicemail-greeting"
          deleteEndpoint="/admin/calling/voicemail-greeting"
          onChange={load}
          testIdPrefix="default-voicemail"
          readOnly={!callPerm.editVoicemailGreeting}
        />

        <AudioFileSection
          title='"Please hold" transfer audio'
          icon={PhoneForwarded}
          description='Played once when a transfer connects ("Please hold while we connect your call").'
          currentUrl={settings.please_hold_url}
          currentFilename={settings.please_hold_filename}
          isSystemDefault={!!settings.please_hold_is_system}
          uploadEndpoint="/admin/calling/please-hold"
          deleteEndpoint="/admin/calling/please-hold"
          onChange={load}
          testIdPrefix="please-hold"
          readOnly={!callPerm.editPleaseHoldAudio}
        />

        <CallStartAudioSection settings={settings} onChange={load} readOnly={!callPerm.editStartDisclaimer} togglesReadOnly={!callPerm.editPlayOnConnect} />
      </div>
    </div>
  );
};

/**
 * Admin section: "Call Start Disclaimer" audio + 3 toggle buttons.
 *
 * The uploaded audio plays once for ALL parties when a call connects.
 * Each call-type toggle is independent and applies app-wide.
 */
const CallStartAudioSection = ({ settings, onChange, readOnly = false, togglesReadOnly = false }) => {
  const { api } = useAuth();
  const [saving, setSaving] = useState(null);

  const setToggle = async (key, value) => {
    if (togglesReadOnly) return;
    setSaving(key);
    try {
      await api.put('/admin/calling/call-start-audio/toggles', { [key]: value });
      toast.success(value ? 'Will play on connect' : 'Disabled');
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(null);
    }
  };

  const toggles = [
    { key: 'team_calls', label: 'Team Calls', icon: Users, on: !!settings.call_start_audio_team_calls },
    { key: 'external_calls', label: 'External Calls', icon: PhoneCall, on: !!settings.call_start_audio_external_calls },
    { key: 'video_calls', label: 'Video Calls', icon: Video, on: !!settings.call_start_audio_video_calls },
  ];

  return (
    <div className="space-y-3">
      <AudioFileSection
        title='Call-start disclaimer'
        icon={Megaphone}
        description='Played once for ALL parties the moment a call connects. Toggle below which call types should play it.'
        currentUrl={settings.call_start_audio_url}
        currentFilename={settings.call_start_audio_filename}
        isSystemDefault={!!settings.call_start_audio_is_system}
        uploadEndpoint="/admin/calling/call-start-audio"
        deleteEndpoint="/admin/calling/call-start-audio"
        onChange={onChange}
        testIdPrefix="call-start-audio"
        readOnly={readOnly}
      />
      <Card data-testid="call-start-audio-toggles">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Play on connect for:</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {toggles.map((t) => {
              const Icon = t.icon;
              const isSaving = saving === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setToggle(t.key, !t.on)}
                  disabled={isSaving || togglesReadOnly}
                  data-testid={`call-start-toggle-${t.key.replace('_', '-')}`}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 px-2 py-3 transition-all ${
                    t.on
                      ? 'border-[#586D52] bg-[#586D52] text-white shadow-md'
                      : 'border-muted bg-white dark:bg-[#1a1a1a] text-muted-foreground hover:border-[#586D52]/50'
                  } ${togglesReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{t.label}</span>
                  <span className={`text-[10px] uppercase tracking-wide ${t.on ? 'text-white/80' : 'text-muted-foreground'}`}>
                    {t.on ? 'On' : 'Off'}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCallingPage;
